/**
 * WelcomeSeoMeta.js
 *
 * SEO + structured data component for the Diet Planner's Screen 1 (Welcome).
 * Mirrors YogSeoMeta.js's patterns: keyed <Helmet> so meta always flushes on
 * change, a word-boundary-safe description trimmer, and JSON-LD structured
 * data appropriate to the page (here: WebApplication + BreadcrumbList,
 * rather than Yog's VideoObject).
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE        = `${SITE_URL}/og-image-diet-planner.jpg`;
const TWITTER_HANDLE  = "@yogashastra";
const PAGE_PATH       = "/diet-planner";

// ─── Safe description trimmer — respects word boundaries ──────────────────────
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// WelcomePageMeta
// ═══════════════════════════════════════════════════════════════════════════════

export const WelcomePageMeta = ({
  title: titleOverride,
  description: descriptionOverride,
} = {}) => {
  const title = titleOverride
    ? `${titleOverride} – ${SITE_NAME}`
    : `AI Diet Planner — Personalized Nutrition in 3 Minutes | ${SITE_NAME}`;

  const description = trimDesc(
    descriptionOverride ||
      "Get a scientifically balanced, Indian-first diet plan powered by AI — free, no sign-up, ready in under 3 minutes. Yoga-aware nutrition from Yoga Shastra."
  );

  const canonical = `${SITE_URL}${PAGE_PATH}`;

  // ── WebApplication JSON-LD ───────────────────────────────────────────────────
  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "YogaShastra AI Diet Planner",
    url: canonical,
    applicationCategory: "HealthApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };

  // ── BreadcrumbList JSON-LD ───────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "AI Diet Planner", item: canonical },
    ],
  };

  // Keyed so Helmet reliably flushes if title/description ever change client-side
  return (
    <Helmet key={canonical}>
      {/* ── Primary meta ── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* ── Open Graph ── */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* ── Twitter card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* ── Structured data ── */}
      <script type="application/ld+json">
        {JSON.stringify(webAppSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};